import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import Navigation from '../components/Navigation';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Shield, CheckCircle, Lock } from 'lucide-react';

const SecurityQuestionsSetup = () => {
  const { user, API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);
  const [setupComplete, setSetupComplete] = useState(false);
  
  const [formData, setFormData] = useState({
    question1: '',
    answer1: '',
    question2: '',
    answer2: '',
    question3: '',
    answer3: ''
  });

  useEffect(() => {
    fetchQuestions();
    checkExistingSetup();
  }, []);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions/list`);
      setQuestions(response.data.questions);
    } catch (error) {
      toast.error('Failed to load security questions');
    } finally {
      setLoading(false);
    }
  };

  const checkExistingSetup = async () => {
    try {
      const response = await axios.get(`${API}/auth/security-questions/status`);
      setHasExisting(response.data.has_security_questions);
    } catch (error) {
      console.error('Failed to check status:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields are filled
    if (!formData.question1 || !formData.answer1 ||
        !formData.question2 || !formData.answer2 ||
        !formData.question3 || !formData.answer3) {
      toast.error('Please select all questions and provide answers');
      return;
    }
    
    // Validate questions are different
    if (formData.question1 === formData.question2 ||
        formData.question1 === formData.question3 ||
        formData.question2 === formData.question3) {
      toast.error('Please select three different questions');
      return;
    }
    
    // Validate answers are at least 2 characters
    if (formData.answer1.length < 2 || formData.answer2.length < 2 || formData.answer3.length < 2) {
      toast.error('Answers must be at least 2 characters');
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(`${API}/auth/security-questions/setup`, formData);
      // Flip the gate so ProtectedRoute lets the user into the app.
      localStorage.setItem('has_security_questions', 'true');
      setSetupComplete(true);
      toast.success('Security questions saved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save security questions');
    } finally {
      setSaving(false);
    }
  };

  const getAvailableQuestions = (excludeQuestions) => {
    return questions.filter(q => !excludeQuestions.includes(q));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50 flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (setupComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
        {user && <Navigation />}
        <div className="container mx-auto px-4 py-8 max-w-xl">
          <Card className="shadow-xl" data-testid="setup-complete-card">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold text-[#2B6B7F] mb-3">
                Security Questions Set Up!
              </h2>
              <p className="text-slate-600 mb-6">
                Your account is now more secure. You can use these questions to reset your password if you ever forget it.
              </p>
              <Button 
                onClick={() => navigate('/dashboard')} 
                className="bg-[#2B6B7F] hover:bg-[#1E4D5F]"
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-teal-50">
      {user && <Navigation />}
      
      <div className="container mx-auto px-4 py-8 max-w-xl">
        <Card className="shadow-xl" data-testid="security-setup-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-[#2B6B7F]/10 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-[#2B6B7F]" />
            </div>
            <CardTitle className="text-2xl font-heading text-[#2B6B7F]">
              {hasExisting ? 'Update Security Questions' : 'Set Up Security Questions'}
            </CardTitle>
            <CardDescription>
              Choose 3 questions and provide answers. These will be used to verify your identity if you need to reset your password.
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {!hasExisting && (
              <div className="mb-6 p-4 bg-[#2B6B7F]/5 border border-[#2B6B7F]/20 rounded-lg" data-testid="security-required-notice">
                <p className="text-sm text-[#2B6B7F]">
                  <strong>Required to continue.</strong> Please choose 3 security questions and
                  provide answers so you can recover your account if you ever forget your password.
                </p>
              </div>
            )}
            {hasExisting && (
              <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> You already have security questions set up. Submitting this form will replace them.
                </p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Question 1 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Question 1</label>
                <Select
                  value={formData.question1}
                  onValueChange={(value) => setFormData({...formData, question1: value})}
                >
                  <SelectTrigger data-testid="question1-select">
                    <SelectValue placeholder="Select a question..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableQuestions([formData.question2, formData.question3]).map((q, idx) => (
                      <SelectItem key={idx} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer..."
                  value={formData.answer1}
                  onChange={(e) => setFormData({...formData, answer1: e.target.value})}
                  data-testid="answer1-input"
                />
              </div>
              
              {/* Question 2 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Question 2</label>
                <Select
                  value={formData.question2}
                  onValueChange={(value) => setFormData({...formData, question2: value})}
                >
                  <SelectTrigger data-testid="question2-select">
                    <SelectValue placeholder="Select a question..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableQuestions([formData.question1, formData.question3]).map((q, idx) => (
                      <SelectItem key={idx} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer..."
                  value={formData.answer2}
                  onChange={(e) => setFormData({...formData, answer2: e.target.value})}
                  data-testid="answer2-input"
                />
              </div>
              
              {/* Question 3 */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Question 3</label>
                <Select
                  value={formData.question3}
                  onValueChange={(value) => setFormData({...formData, question3: value})}
                >
                  <SelectTrigger data-testid="question3-select">
                    <SelectValue placeholder="Select a question..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableQuestions([formData.question1, formData.question2]).map((q, idx) => (
                      <SelectItem key={idx} value={q}>{q}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="text"
                  placeholder="Your answer..."
                  value={formData.answer3}
                  onChange={(e) => setFormData({...formData, answer3: e.target.value})}
                  data-testid="answer3-input"
                />
              </div>
              
              <div className="pt-4">
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#2B6B7F] hover:bg-[#1E4D5F]"
                  disabled={saving}
                  data-testid="save-security-questions-btn"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Security Questions'}
                </Button>
              </div>
            </form>
            
            <p className="text-xs text-slate-500 mt-4 text-center">
              Remember your answers! They are case-insensitive but must match exactly.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SecurityQuestionsSetup;
